<script setup lang="ts">
const props = withDefaults(defineProps<{ rounded?: string; size?: string }>(), {
  rounded: 'rounded-full',
  size: 'size-11 xs:size-12',
});

const { t } = useI18n();
const localePath = useLocalePath();
const { isLoggedIn, logout } = useAuth();
const client = useClient();
const isLoggingOut = ref(false);

const handleLogout = async () => {
  try {
    isLoggingOut.value = true;
    await logout();

    const businessStore = useBusinessStore();
    const { selectedLocation } = storeToRefs(businessStore);

    if (selectedLocation.value?.identifier) {
      await navigateTo(localePath(`/${selectedLocation.value.identifier}`));
    } else {
      await navigateTo(localePath(`/`));
    }
  } catch (err) {
    console.error(err);
  } finally {
    isLoggingOut.value = false;
  }
};

const clientItems = [
  [
    {
      name: client.value?.name,
      full_mobile: client.value?.full_mobile_number,
      slot: "account",
      disabled: true,
    },
  ],
  [
    {
      label: t("label.profile"),
      click: () => navigateTo(localePath("/account")),
    },
    {
      label: t("label.bookings"),
      click: () => navigateTo(localePath("/account/bookings")),
    },
    {
      label: t("label.wallet"),
      click: () => navigateTo(localePath("/account/wallet")),
    },
    {
      label: t("label.visited_locations"),
      click: () => navigateTo(localePath("/account/visited-locations")),
    },
    {
      label: t("label.product_orders"),
      click: () => navigateTo(localePath("/account/product-orders")),
    },
    {
      label: t("label.settings"),
      click: () => navigateTo(localePath("/account/settings")),
    },
  ],
  [
    {
      label: t("label.logout"),
      class: "hover:bg-red-500 text-red-500 hover:text-white",
      click: async () => await handleLogout(),
    },
  ],
];
</script>

<template>
  <UIcon
    v-if="isLoggingOut"
    name="i-approved-loading-circle"
    class="text-dodger-blue-600 size-11 xs:size-12 animate-spin"
  />
  <UDropdown
    v-else-if="isLoggedIn && !isLoggingOut"
    :items="clientItems"
    :ui="{
      padding: 'p-1.5',
      width: 'w-54',
      rounded: 'rounded-xl',
      item: {
        inactive: 'text-[#25265E]',
        rounded: 'rounded-lg',
        disabled: 'cursor-auto opacity-100 px-0',
      },
    }"
  >
    <div
      class="flex items-center justify-center overflow-hidden"
      :class="[props.size, props.rounded]"
    >
      <span
        class="text-white flex items-center justify-center font-medium leading-none uppercase size-full bg-primary-500"
      >
        {{ client?.short_name?.replace(" ", "") }}
      </span>
    </div>
    <template #account="{ item }">
      <div class="flex items-center min-w-0">
        <div
          class="size-10 xs:size-11 flex items-center justify-center overflow-hidden"
          :class="props.rounded"
        >
          <span
            class="text-white flex items-center justify-center font-medium leading-none uppercase size-full bg-primary-500"
          >
            {{ client?.short_name?.replace(" ", "") }}
          </span>
        </div>
        <div class="text-start flex flex-col ps-2.5 min-w-0">
          <h2 class="text-[#25265E] font-medium capitalize truncate">
            {{ item.name }}
          </h2>
          <span class="text-[#535A8C] capitalize truncate" dir="ltr">{{
            item.full_mobile
          }}</span>
        </div>
      </div>
    </template>
    <template #item="{ item }">
      <div class="flex flex-col text-start px-1 py-0.5">
        <p class="text-sm font-medium capitalize">
          {{ item.label }}
        </p>
      </div>
    </template>
  </UDropdown>
</template>
